export interface Event {
  event_id: string;
  event_name: string;
  description: string;
  date: string;
  venue_id: string;
  duration_minutes?: number;
}

/**
 * Format a Date object as YYYYMMDDTHHMMSSZ for ICS / Google Calendar
 */
const formatUTCDate = (d: Date) =>
  d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

/**
 * Create ICS content for an event
 * @param event Event data
 * @param location Optional venue address
 */
export function createICSContent(event: Event, location?: string) {
  // Treat Supabase timestamptz as UTC
  const startDate = new Date(event.date);
  const endDate = new Date(
    startDate.getTime() + (event.duration_minutes ?? 120) * 60_000,
  );

  return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//YourApp//EN
BEGIN:VEVENT
UID:${event.event_id}@yourapp.com
SUMMARY:${event.event_name}
DESCRIPTION:${event.description}
DTSTART:${formatUTCDate(startDate)}
DTEND:${formatUTCDate(endDate)}
LOCATION:${location ?? 'TBD'}
END:VEVENT
END:VCALENDAR`;
}

/**
 * Download an ICS file for the event
 * @param event Event data
 * @param location Optional venue address
 */
export function downloadICS(event: Event, location?: string) {
  const icsContent = createICSContent(event, location);

  const blob = new Blob([icsContent], {
    type: 'text/calendar;charset=utf-8',
  });

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${event.event_name}.ics`;
  a.click();
  window.URL.revokeObjectURL(url);
}

/**
 * Get a Google Calendar link for the event
 * @param event Event data
 * @param location Venue address
 */
export function getGoogleCalendarLink(event: Event, location: string) {
  const startDate = new Date(event.date);
  const endDate = new Date(
    startDate.getTime() + (event.duration_minutes ?? 120) * 60_000,
  );

  return `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
    event.event_name,
  )}&dates=${formatUTCDate(startDate)}/${formatUTCDate(endDate)}&details=${encodeURIComponent(
    event.description ?? '',
  )}&location=${encodeURIComponent(location ?? '')}`;
}
